import { queryOptions, useQuery, type UseBaseQueryOptions } from '@tanstack/react-query'
import { type StoreParams, useStoreParams } from '@/features/shared/utils/dataFetching/storeParams'
import {
  fetchCartFee,
  fetchNycOrdinanceFee,
  fetchRetailerFees,
} from '@/features/shop/services/Fees/fetchers'
import { useUserShoppingStore } from '@/features/shop/services/ShoppingStore/hooks'
import { useGlobalDefaultShoppingAddress } from '@/features/account/services/Addresses/hooks'

export type FeesQueryKey = readonly ['Fees', StoreParams]

export const feesQueryOptions = (storeParams: StoreParams) =>
  queryOptions({
    queryKey: ['Fees', storeParams] as const,
    queryFn: fetchCartFee,
  })

export const useQueryCartFees = () => {
  const storeParams = useStoreParams()
  const { metro_id } = storeParams

  return useQuery({
    ...feesQueryOptions(storeParams),
    enabled: Boolean(metro_id),
  })
}

export const retailerFeesQueryOptions = queryOptions({
  queryKey: ['RetailerFees'] as const,
  queryFn: fetchRetailerFees,
})

export const useQueryRetailerFees = ({
  notifyOnChangeProps,
}: {
  notifyOnChangeProps?: UseBaseQueryOptions['notifyOnChangeProps']
} = {}) => {
  return useQuery({
    ...retailerFeesQueryOptions,
    notifyOnChangeProps,
  })
}

export type NycOrdinanceFeeParams = {
  storeZips: string[]
  customerZip: string
}
export type NycOrdinanceFeeQueryKey = readonly ['NycOrdinanceFee', NycOrdinanceFeeParams]

export const nycOrdinanceFeeQueryOptions = (params: NycOrdinanceFeeParams) =>
  queryOptions({
    queryKey: ['NycOrdinanceFee', params] as const,
    queryFn: fetchNycOrdinanceFee,
  })

export const useQueryNycOrdinanceFee = () => {
  const { data: selectedStore } = useUserShoppingStore()
  const storeZip = selectedStore?.address_zip_code
  const shoppingAddress = useGlobalDefaultShoppingAddress()
  const customerZip = shoppingAddress.zip_code

  return useQuery({
    ...nycOrdinanceFeeQueryOptions({
      storeZips: [storeZip || ''],
      customerZip,
    }),
    enabled: Boolean(storeZip && customerZip),
  })
}
